import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { TotalMachineOperatingHoursDatum } from '../interfaces/MachineOperatingHoursDashboardChart.types';
import { MachineOperatingHoursDashboardChartBar } from '../MachineOperatingHoursDashboardChartBar/MachineOperatingHoursDashboardChartBar';
import { StyledMachineOperatingHoursDashboardChartElement } from './MachineOperatingHoursDashboardChartElement.styles';
import { Maybe } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { buildMachineDetailsTabPath } from 'app/modules/machine-inventory/machine-details-panel/hooks/useMachineDetailsTabFromUrl';
import { RoutePaths } from 'config/route-paths';

export interface MachineOperatingHoursDashboardChartElementProps {
  className: string;
  chartBarId: string;
  totalMachineOperatingHoursDatum: TotalMachineOperatingHoursDatum;
  operatingHoursMaximum: number;
}

export const MachineOperatingHoursDashboardChartElement = ({
  className,
  chartBarId,
  totalMachineOperatingHoursDatum,
  operatingHoursMaximum,
}: MachineOperatingHoursDashboardChartElementProps): Maybe<JSX.Element> => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id, name } = totalMachineOperatingHoursDatum;

  const handleOpenMachineDetails = (): void => {
    navigate(buildMachineDetailsTabPath(id, 'OVERVIEW', RoutePaths.MACHINE_DASHBOARD));
  };

  const actualTotalOperatingTimeMs = totalMachineOperatingHoursDatum.operatingTimeForPeriod?.actualTotalOperatingTimeMs;

  if (!totalMachineOperatingHoursDatum.name || isNil(actualTotalOperatingTimeMs) || isNil(operatingHoursMaximum)) {
    return null;
  }

  const operatingHoursAsHours = DateTime.formatDurationByMilliseconds({
    ms: actualTotalOperatingTimeMs,
    showMinutes: true,
    showSeconds: false,
    padWithZeros: true,
  });
  const operatingHoursWithMinutesAndSeconds = DateTime.formatDurationByMilliseconds({
    ms: actualTotalOperatingTimeMs,
    showMinutes: true,
    showSeconds: true,
    padWithZeros: true,
  });

  return (
    <StyledMachineOperatingHoursDashboardChartElement
      className={classnames('machine-operating-hours-dashboard-chart-element', className)}
    >
      <div className="machine-operating-hours-dashboard-chart-element__name" title={name}>
        <Button
          type="link"
          onClick={handleOpenMachineDetails}
          className="machine-operating-hours-dashboard-chart-element__button--no-styling"
        >
          {name}
        </Button>
      </div>
      <div className="machine-operating-hours-dashboard-chart-element__bar">
        <MachineOperatingHoursDashboardChartBar
          key={chartBarId}
          totalMachineOperatingHoursDatum={totalMachineOperatingHoursDatum}
          operatingHoursMaximum={operatingHoursMaximum}
        />
      </div>
      <div
        className="machine-operating-hours-dashboard-chart-element__operating-hours"
        title={operatingHoursWithMinutesAndSeconds}
      >
        {operatingHoursAsHours} {t('cleaningReportList.exact.table.suffixes.duration')}
      </div>
    </StyledMachineOperatingHoursDashboardChartElement>
  );
};
